<template>
  <div class="vh-100 d-flex justify-content-center">
    <div class="form-access my-auto">
      <form autocomplete="off">
        <span>Create Demo</span>
        <div class="form-group">
          <label for="demo-account-name">Name</label>
          <input id="demo-account-name" type="text" name="demo-account-name" autocomplete="off" class="form-control" :class="{ 'red-border': nameTouched && !isValidName, 'green-border': nameTouched && isValidName }" placeholder="e.g. Phil Philips" v-model="name" @blur="nameTouched = true" required />
          <div v-if="nameTouched && !isValidName" class="validation-message">Name must contain valid letters (min. 2 characters, no numbers).</div>
        </div>
        <div class="form-group">
          <label for="demo-account-email">Email</label>
          <input id="demo-account-email" type="email" name="demo-account-email" autocomplete="off" class="form-control" :class="{ 'red-border': emailTouched && !isValidEmail, 'green-border': emailTouched && isValidEmail }" placeholder="e.g. your.mail.address@gmail.com" v-model="email" @blur="emailTouched = true" required />
          <div v-if="emailTouched && !isValidEmail" class="validation-message">Enter a valid email address (e.g. name@domain.com).</div>
        </div>
        <div class="form-group">
          <label for="demo-account-phone">Phone</label>
          <input id="demo-account-phone" type="tel" name="demo-account-phone" autocomplete="off" class="form-control" :class="{ 'red-border': phoneTouched && !isValidPhone, 'green-border': phoneTouched && isValidPhone }" placeholder="e.g. +359 (888) 123-456 or +1 (555) 019-2834" v-model="phone" @blur="phoneTouched = true" required />
          <div v-if="phoneTouched && !isValidPhone" class="validation-message">Enter a valid phone number with digits, +, spaces, or () (min. 7 digits).</div>
        </div>
        <div class="form-group">
          <label for="demo-account-password">Password</label>
          <input id="demo-account-password" type="password" name="demo-account-password" autocomplete="new-password" class="form-control" :class="{ 'red-border': passwordTouched && !isValidPassword, 'green-border': passwordTouched && isValidPassword }" placeholder="e.g. Password" v-model="password" @blur="passwordTouched = true" required />
          <div v-if="passwordTouched" class="password-requirements">
            <strong>Password requirements:</strong>
            <div :class="{ 'requirement-met': passwordHasMinLength }"><span>{{ passwordHasMinLength ? "✓" : "×" }}</span> At least 8 characters</div>
            <div :class="{ 'requirement-met': passwordHasLowercase }"><span>{{ passwordHasLowercase ? "✓" : "×" }}</span> At least 1 lowercase letter (a-z)</div>
            <div :class="{ 'requirement-met': passwordHasUppercase }"><span>{{ passwordHasUppercase ? "✓" : "×" }}</span> At least 1 uppercase letter (A-Z)</div>
            <div :class="{ 'requirement-met': passwordHasNumber }"><span>{{ passwordHasNumber ? "✓" : "×" }}</span> At least 1 number (0-9)</div>
            <div :class="{ 'requirement-met': passwordHasSpecialCharacter }"><span>{{ passwordHasSpecialCharacter ? "✓" : "×" }}</span> At least 1 special character (!@#$%^&amp;*)</div>
          </div>
        </div>
        <button type="submit" class="btn btn-primary" :disabled="!submitEnabled" @click.prevent="registerUser()">Create</button>
      </form>
      <h2>
        <router-link :to="{ name: 'Login', query: { accountType: 'demo' } }">{{ t("loginLabel") }}</router-link>
      </h2>
    </div>
  </div>
</template>

<script>
import http from "@/api/http-common";
import HttpUtils from "@/assets/js/http_utils.js";
import I18n from "@/lang/i18n.js";

function validateEmail(mail) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mail.trim());
}

export default {
  data() {
    return {
      name: "",
      email: "",
      phone: "",
      password: "",
      nameTouched: false,
      emailTouched: false,
      phoneTouched: false,
      passwordTouched: false,
      confirmedPasswordClass: "black-color"
    }
  },
  methods: {
    t(key, ...args) {
      return I18n.t(key, "EN", ...args);
    },
    async registerUser() {
      if (!this.submitEnabled) {
        return;
      }

      try {
        // always demo
        const res = await http.getApiClient(0).get("", {
          params: {
            a: "demo_reg_ex",
            em: encodeURIComponent(this.email),
            ph: encodeURIComponent(this.phone),
            name: encodeURIComponent(this.name),
            pw: encodeURIComponent(this.password)
          }});
        if (res) {
          // check answer
          const errorCode = HttpUtils.getStatusMessage(res.data.ec, "EN");          
          if (errorCode === "") {
            this.$toast.success("You have registered with EFOCS. Please follow the link in your email to activate your account", {
              "duration": 10000,
              "pauseOnHover": true
            });
            this.$router.push({ name: "Login" });
          } else {
            this.$toast.error(errorCode, {
              "duration": 5000,
              "pauseOnHover": true
            });
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  },
  computed: {
    isValidName() {
      const value = this.name.trim();
      const lettersOnly = value.replace(/[^\p{L}]/gu, "");
      return 2 <= lettersOnly.length && /^[\p{L}\s'-]+$/u.test(value);
    },
    isValidEmail() {
      return validateEmail(this.email);
    },
    isValidPhone() {
      const value = this.phone.trim();
      const digitCount = (value.match(/\d/g) || []).length;
      return 0 < value.length && /^[0-9+ ()-]+$/.test(value) && 7 <= digitCount;
    },
    passwordHasMinLength() {
      return 8 <= this.password.length;
    },
    passwordHasLowercase() {
      return /[a-z]/.test(this.password);
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.password);
    },
    passwordHasNumber() {
      return /[0-9]/.test(this.password);
    },
    passwordHasSpecialCharacter() {
      return /[!@#$%^&*]/.test(this.password);
    },
    isValidPassword() {
      return this.passwordHasMinLength && this.passwordHasLowercase &&
        this.passwordHasUppercase && this.passwordHasNumber &&
        this.passwordHasSpecialCharacter;
    },
    submitEnabled() {
      return this.isValidName && this.isValidEmail && this.isValidPhone && this.isValidPassword;
    }
  }
}
</script>

<style scoped>

.form-access form {
  min-width: 480px;
}

.form-access .form-group {
  margin-bottom: 0;
}

.form-access .form-group + .form-group {
  margin-top: 1rem;
}

.form-access .btn-primary {
  background-color: #0088aa;
  border-color: #0088aa;
}

.form-access .btn-primary:hover,
.form-access .btn-primary:focus,
.form-access .btn-primary:active {
  background-color: #006f8c;
  border-color: #006f8c;
}

.form-access a {
  color: #0088aa;
}

.form-access a:hover,
.form-access a:focus {
  color: #006f8c;
}


input.red-border {
  border: 2px solid red;
}

input.green-border {
  border: 2px solid green;
}

input.black-border {
  border: 2px solid black;
}

.form-access label {
  display: block;
  margin-bottom: 0.35rem;
}

.validation-message {
  color: #ff0000;
  font-size: 0.85rem;
  margin-top: 0.35rem;
}

.password-requirements {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.8;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
}

.password-requirements strong {
  color: #1c3557;
  display: block;
  margin-bottom: 0.15rem;
}

.password-requirements span {
  color: #6c757d;
  display: inline-block;
  font-size: 1.1rem;
  margin-right: 0.35rem;
  width: 1rem;
}

.password-requirements .requirement-met,
.password-requirements .requirement-met span {
  color: #198754;
}
</style>
